import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import LeadPurge from '@/components/LeadPurge';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Loading,
  PageHeader,
  Select,
} from '@/components/ui';
import { formatPhone, formatRelative } from '@/lib/format';

interface Lead {
  id: string;
  phone: string;
  first_name: string | null;
  last_name: string | null;
  campaign: string | null;
  vicidial_lead_id: string | null;
  do_not_call: boolean;
  last_called_at: string | null;
  created_at: string;
  client_name: string;
  tenant_id: string;
  integration_name: string | null;
  calls: number;
  ever_qualified: boolean | null;
  ever_reached: boolean | null;
  ever_transferred: boolean | null;
  last_disposition: string | null;
}

interface ClientOption {
  id: string;
  name: string;
}

/** Every lead on the platform, filterable down to one client. */
export default function Leads() {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [tenantId, setTenantId] = useState('');
  /**
   * What happened to the lead, not whether it exists.
   *
   * A `leads` row is written for EVERY number the dialer sends, so on a cold
   * list most of them are numbers nobody ever spoke to. That is a legitimate
   * thing to keep -- it is the dialer's list with outcomes attached -- and it
   * is not what anybody means by "leads", which is why this page was reported
   * as "showing many calls" when it was showing exactly what it was asked to.
   *
   * So it opens on REACHED. A number whose only calls were dead air or an
   * answering machine is a phone number, not a lead.
   */
  const [outcome, setOutcome] = useState('reached');
  const [days, setDays] = useState(30);

  // Debounce so a search does not fire a request per keystroke. `useEffect`,
  // not `useMemo`: a memo's cleanup never runs, so the timer would leak.
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(search), 300);
    return () => window.clearTimeout(timer);
  }, [search]);

  const clients = useQuery({
    queryKey: ['admin-clients-select'],
    queryFn: () => api.get<{ data: ClientOption[] }>('/admin/clients', { page_size: 200 }),
    staleTime: 60_000,
  });

  const query = useQuery({
    queryKey: ['admin-leads', debounced, tenantId, outcome, days],
    queryFn: () =>
      api.get<{
        data: Lead[];
        count: number;
        totals: Record<string, number>;
      }>('/admin/leads', {
        search: debounced || undefined,
        tenant_id: tenantId || undefined,
        outcome,
        days,
      }),
  });

  return (
    <div className="w-full">
      <PageHeader
        title="Leads"
        description="Every number the dialer has sent, and what came of it."
        actions={
          <div className="flex items-center gap-2">
            {query.data?.totals ? (
              <span className="text-sm text-ink-500">
                <strong className="text-ink-900 dark:text-ink-50">
                  {query.data.totals.all.toLocaleString()}
                </strong>{' '}
                leads
              </span>
            ) : null}
            {/* Named client only. There is no "every client" purge: one
                mistake there is every list on the platform, and no
                confirmation dialog makes that a reasonable button to have. */}
            {tenantId ? (
              <LeadPurge
                path="/admin/leads/purge"
                tenantId={tenantId}
                queryKey="admin-leads"
              />
            ) : (
              <span className="text-xs text-ink-400">
                Choose a client to delete old leads
              </span>
            )}
          </div>
        }
      />

      <Card className="mb-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or phone…"
            aria-label="Search leads"
          />
          <Select value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
            <option value="">All clients</option>
            {(clients.data?.data ?? []).map((client) => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </Select>
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last year</option>
          </Select>
          <Select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="reached">Someone answered and spoke</option>
            <option value="qualified">Qualified</option>
            <option value="dnc">Do not call</option>
            <option value="all">Every number dialled</option>
          </Select>
        </div>

        {/* Said plainly, because filtering to "reached" on a list that is
            mostly dead air otherwise looks like the page being broken. */}
        {!!query.data?.totals && (
          <p className="mt-3 text-xs text-ink-500">
            {query.data.totals.all} numbers dialled ·{' '}
            <strong>{query.data.totals.reached} reached a person</strong> ·{' '}
            {query.data.totals.qualified} qualified · {query.data.totals.dnc} on
            do-not-call.
            {query.data.totals.all > query.data.totals.reached && (
              <>
                {' '}
                The other{' '}
                {query.data.totals.all - query.data.totals.reached} were dead
                air, an answering machine, or never answered — phone numbers
                rather than leads.
              </>
            )}
          </p>
        )}
      </Card>

      <Card padded={false}>
        {query.isPending ? (
          <Loading />
        ) : query.error ? (
          <ErrorState error={query.error} retry={() => query.refetch()} />
        ) : query.data.data.length === 0 ? (
          <EmptyState
            title="No leads match"
            description={
              debounced || tenantId || outcome !== 'all'
                ? 'Try widening the filters or the time window.'
                : 'Leads appear here as calls are handled.'
            }
          />
        ) : (
          <div className="scroll-x">
            <table className="glee-table">
              <thead>
                <tr>
                  <th>Lead</th>
                  <th>Client</th>
                  <th>Campaign</th>
                  <th>Dialer</th>
                  <th className="text-right">Calls</th>
                  <th>Status</th>
                  <th className="text-right">Last called</th>
                </tr>
              </thead>
              <tbody>
                {query.data.data.map((lead) => {
                  const name = [lead.first_name, lead.last_name].filter(Boolean).join(' ');
                  return (
                    <tr key={lead.id}>
                      <td>
                        <div className="font-medium text-ink-900 dark:text-ink-50">
                          {name || <span className="text-ink-400">Unnamed</span>}
                        </div>
                        <div className="font-mono text-2xs text-ink-500">
                          {formatPhone(lead.phone)}
                          {lead.vicidial_lead_id && ` · #${lead.vicidial_lead_id}`}
                        </div>
                      </td>
                      <td>
                        <Link
                          to={`/clients/${lead.tenant_id}`}
                          className="text-xs text-ink-600 hover:text-brand-600 dark:text-ink-300 dark:hover:text-brand-400"
                        >
                          {lead.client_name}
                        </Link>
                      </td>
                      <td className="text-xs text-ink-600 dark:text-ink-300">
                        {lead.campaign ?? '—'}
                      </td>
                      <td className="text-xs text-ink-600 dark:text-ink-300">
                        {lead.integration_name ?? '—'}
                      </td>
                      <td className="text-right tabular-nums text-ink-700 dark:text-ink-200">
                        {lead.calls}
                      </td>
                      <td>
                        <div className="flex flex-wrap gap-1">
                          {lead.do_not_call && <Badge tone="red">DNC</Badge>}
                          {lead.ever_qualified && <Badge tone="green">Qualified</Badge>}
                          {lead.ever_transferred && (
                            <Badge tone="blue">Transferred</Badge>
                          )}
                          {!lead.ever_reached && lead.calls > 0 && (
                            <Badge tone="gray">Never spoke</Badge>
                          )}
                          {!lead.do_not_call && !lead.ever_qualified && (
                            <span className="text-xs text-ink-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="text-right text-xs tabular-nums text-ink-500">
                        {formatRelative(lead.last_called_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
